/*
  Klikk og hent (M-20261023-klikk-og-hent, spec 2.4, D1-D2, D8-D9): the pickup decision (Storefront on hand; any failed
  read is 10 days, A2), the cart sync, <bjerve-butikklager> and the cart block. Markup: snippets/bjerve-delivery-note.liquid.
*/

const API_PATH = '/api/2026-07/graphql.json';
const TIMEOUT_MS = 4000;
const RETRY_MS = 1000;
const FEW_LEFT = 2;
const CHANGE = 'bjerve-henting:endret';

/** The five shops' numeric location ids, as snippets/bjerve-butikker.liquid (test-butikklager asserts it). */
export const PICKUP_LOCATION_IDS = Object.freeze(['71553057026', '92792848642', '71552991490', '71042040066', '71553024258']);

// 250 is the page maximum (largest product: 234 variants); a variant past it gets 10 days.
const AVAILABILITY = 'id storeAvailability(first:10){nodes{quantityAvailable location{id}}}';
const QUERY = `query BjerveButikklager($handle:String!){product(handle:$handle){variants(first:250){nodes{${AVAILABILITY}}}}}`;
const NODES = `query BjerveVarianter($ids:[ID!]!){nodes(ids:$ids){...on ProductVariant{${AVAILABILITY}}}}`;
const MUTATION = `mutation BjerveHenting($id:ID!,$b:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$id,buyerIdentity:$b){
cart{lines(first:100){nodes{merchandise{...on ProductVariant{${AVAILABILITY}}}}}}userErrors{code}}}`;

/** The placeholders the Liquid `t` filter is given, replaced here. */
export const SENTINEL = Object.freeze({ town: '__BY__', store: '__BUTIKK__', quantity: '__ANTALL__' });

/** @typedef {{ quantityAvailable: unknown, location: { id?: string } | null }[] | null | undefined} Nodes */

const numericId = (id) => String(id ?? '').split('/').pop();

/** onHand(nodes: Nodes, locationId): on-hand quantity at one of the five shops, null without a finite read. */
export function onHand(nodes, locationId) {
  if (!Array.isArray(nodes) || !PICKUP_LOCATION_IDS.includes(locationId)) return null;
  const quantity = nodes.find((n) => n?.location && numericId(n.location.id) === locationId)?.quantityAvailable;
  return typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : null;
}

/** D2: '2-timer' exactly when that shop holds the quantity asked (>= 1), else '10-virkedager'. */
export function readyTime(nodes, locationId, quantity) {
  const held = onHand(nodes, locationId);
  return Number.isFinite(quantity) && quantity >= 1 && held !== null && held >= quantity ? '2-timer' : '10-virkedager';
}

/** A cart's time at a shop: shipped /cart.js lines, quantity summed per variant, the slowest; null with none. */
export function cartReadyTime(lines, byVariant, locationId) {
  const asked = new Map();
  for (const line of lines ?? []) {
    if (!line?.requires_shipping) continue;
    const id = String(line.variant_id);
    asked.set(id, (asked.get(id) ?? 0) + Number(line.quantity));
  }
  if (asked.size === 0) return null;
  for (const [id, quantity] of asked) {
    if (readyTime(byVariant?.get(id) ?? null, locationId, quantity) !== '2-timer') return '10-virkedager';
  }
  return '2-timer';
}

const fill = (template, values) =>
  Object.entries(SENTINEL).reduce((text, [key, mark]) => text.replaceAll(mark, () => String(values[key] ?? '')), String(template ?? ''));

/** D8's pickup words: the chosen shop, else the one holding most (ties by town, nb); "Kun n igjen"; the offer. */
export function buildPickup({ nodes, locationId, quantity }, strings, shops) {
  const chosen = shops.find((shop) => shop.id === locationId) ?? null;
  const holders = shops
    .filter((shop) => readyTime(nodes, shop.id, quantity) === '2-timer')
    .map((shop) => ({ ...shop, held: onHand(nodes, shop.id) }))
    .sort((a, b) => b.held - a.held || a.town.localeCompare(b.town, 'nb'));
  const shown = chosen ?? holders[0] ?? null;
  const pickup = {
    locationId: shown?.id ?? null,
    time: '10-virkedager',
    lines: [],
    suggestion: null,
    picker: chosen ? strings.endre : strings.velgButikk,
    hold: strings.hold,
  };
  if (!shown) {
    pickup.lines.push(strings.ingenValgt);
    return pickup;
  }
  pickup.time = readyTime(nodes, shown.id, quantity);
  if (pickup.time === '2-timer') {
    pickup.lines.push(fill(strings.pickup, { store: shown.name }));
    const held = onHand(nodes, shown.id);
    if (held <= FEW_LEFT) pickup.lines.push(fill(strings.fewLeft, { town: shown.town, quantity: held }));
    return pickup;
  }
  pickup.lines.push(fill(strings.klar10, { store: shown.name }));
  const best = holders[0];
  if (best) {
    pickup.suggestion = {
      text: fill(strings.travelt, { town: best.town }),
      button: fill(strings.velgBy, { town: best.town }),
      locationId: best.id,
    };
  }
  return pickup;
}

/** Nodes per numeric id of the variants that carry storeAvailability. */
function byId(variants) {
  const map = new Map();
  for (const v of variants ?? []) if (Array.isArray(v?.storeAvailability?.nodes)) map.set(numericId(v.id), v.storeAvailability.nodes);
  return map;
}

/** Nodes per variant from a product response; a missing list throws, never "no shop has it". */
export function parseStoreAvailability(body) {
  const variants = body?.data?.product?.variants?.nodes;
  if (!Array.isArray(variants)) throw new Error(`Storefront API: ${body?.errors?.[0]?.message ?? 'no product in response'}`);
  const map = byId(variants);
  if (map.size < variants.length) throw new Error('Storefront API: no storeAvailability on a variant');
  return map;
}

const failure = (status, retry) => Object.assign(new Error(String(status)), { retry });

/** One JSON request that aborts at 4 000 ms; the error names the status only. */
async function once(url, init) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const lost = () => failure(controller.signal.aborted ? 'tidsavbrudd' : 'nett', true);
  try {
    const response = await fetch(url, { credentials: 'same-origin', ...init, signal: controller.signal }).catch(() => {
      throw lost();
    });
    if (!response.ok) throw failure(response.status, response.status === 429 || response.status >= 500);
    return await response.json().catch(() => {
      throw controller.signal.aborted ? lost() : failure('ugyldig svar', false);
    });
  } finally {
    clearTimeout(timer);
  }
}

/** `once` with one retry after 1 s: network error, timeout, 429 or 5xx (writes set values). */
async function call(url, init) {
  try {
    return await once(url, init);
  } catch (error) {
    if (!error.retry) throw error;
    await new Promise((resolve) => setTimeout(resolve, RETRY_MS));
    return once(url, init);
  }
}

const post = (send, url, body, token) =>
  send(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token && { 'X-Shopify-Storefront-Access-Token': token }) },
    body: JSON.stringify(body),
  });

/** D1, D5: the delivery preference on 'gid://shopify/Cart/' + the full token, then _hentebutikk and _hentetid. */
export async function syncCart(mode, locationId, token) {
  if (mode === 'henting' ? !PICKUP_LOCATION_IDS.includes(locationId) : mode !== 'hjem') throw failure('ugyldig valg', false);
  const cart = await call('/cart.js', {});
  const items = Array.isArray(cart?.items) ? cart.items : [];
  if (!items.some((line) => line?.requires_shipping)) return { status: 'ingen', tid: null };
  const delivery = mode === 'henting' ? { deliveryMethod: ['PICK_UP'], pickupHandle: [locationId] } : { deliveryMethod: ['SHIPPING'] };
  const variables = { id: `gid://shopify/Cart/${cart.token}`, b: { preferences: { delivery } } };
  const payload = (await post(call, API_PATH, { query: MUTATION, variables }, token))?.data?.cartBuyerIdentityUpdate;
  if (!payload || payload.userErrors?.length) throw failure('userErrors', false);
  const tid = mode === 'henting' ? cartReadyTime(items, byId(payload.cart?.lines?.nodes?.map((l) => l?.merchandise)), locationId) : null;
  await post(call, '/cart/update.js', { attributes: { _hentebutikk: mode === 'henting' ? locationId : 'hjem', _hentetid: tid ?? '' } });
  return { status: 'lagret', tid };
}

/** A Storefront read without retry; the error is "Storefront API: <status>". */
const read = (query, variables, token) =>
  post(once, API_PATH, { query, variables }, token).catch((error) => {
    throw new Error(`Storefront API: ${/^\d+$/.test(error.message) ? `HTTP ${error.message}` : error.message}`);
  });

/** One read per handle and page view (variant and quantity changes reuse it); a failure is not kept. */
const requests = new Map();

export function requestStoreAvailability(handle, token) {
  const cached = requests.get(handle);
  if (cached) return cached;
  const request = read(QUERY, { handle }, token).then(parseStoreAvailability);
  requests.set(handle, request);
  request.catch(() => requests.delete(handle));
  return request;
}

/** readVariants(ids: numeric variant ids, token): one nodes(ids:) read (cards, cart), nodes per numeric id. */
export async function readVariants(ids, token) {
  const body = await read(NODES, { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) }, token);
  if (!Array.isArray(body?.data?.nodes)) throw new Error('Storefront API: no nodes');
  return byId(body.data.nodes);
}

let pageShops;

/** The shops of #bjerve-henting-data, kept to the five ids. */
export function shopsOfPage() {
  try {
    pageShops ??= JSON.parse(document.getElementById('bjerve-henting-data').textContent).butikker.filter((s) => PICKUP_LOCATION_IDS.includes(s?.id));
  } catch {
    pageShops = []; // no config: no shop is named
  }
  return pageShops;
}

/** Text into a [data-bjerve-*] part by textContent, hidden when empty. */
function show(root, name, text) {
  const part = root.querySelector(`[data-bjerve-${name}]`);
  if (!part) return;
  part.textContent = text || '';
  part.hidden = !text;
}

/** The cart block (cart page, drawer): the chosen shop, its address, the cart's time there and Endre. */
export function mountCart(root, token) {
  const h = () => window.bjerveHenting;
  const q = (s) => root.querySelector(s);
  const t = (name) => root.getAttribute(`data-t-${name}`);
  let cart;
  let loading;
  const load = () =>
    (loading ??= call('/cart.js', {})
      .then(async ({ items }) => {
        const lines = items.filter((l) => l.requires_shipping);
        cart = { lines, byVariant: await readVariants([...new Set(lines.map((l) => `${l.variant_id}`))], token) };
      })
      .catch((error) => {
        cart = {};
        console.warn(`bjerve-henting: ${error.message}`);
      })
      .then(render));
  const shop = () => shopsOfPage().find((s) => s.id === h()?.locationId);
  const timeAt = (id) => cartReadyTime(cart?.lines, cart?.byVariant, id) || '10-virkedager';
  const picker = (apner) => load().then(() => h().apneVelger({ apner, lager: (id) => ({ tid: timeAt(id), antall: null }), etterLukk: render }));
  function render() {
    if (!root.isConnected) return document.removeEventListener(CHANGE, render);
    const s = shop();
    const time = s && timeAt(s.id);
    show(root, 'butikk', s?.name);
    show(root, 'adresse', s && `${s.street}, ${s.postcode} ${s.town}`);
    show(root, 'henting-tekst', t(!s ? 'ingen-valgt' : time === '2-timer' ? 'tid-2' : 'tid-10'));
    show(root, 'velg-butikk', t(s ? 'endre' : 'velg-butikk'));
    q('[value=hjem]').checked = !(q('[value=henting]').checked = !!s && h().mode === 'henting');
    root.dataset.state = !s ? 'ingen' : !cart ? 'laster' : time === '2-timer' ? 'holder' : 'ikke';
    if (s) load();
  }
  root.addEventListener('change', ({ target: { value } }) => {
    if (value === 'hjem') h().velg('hjem');
    else if (!h().velg('henting', shop()?.id)) picker(q('[data-bjerve-velg-butikk]'));
  });
  root.addEventListener('click', ({ target }) => target.closest('[data-bjerve-velg-butikk]') && picker(target));
  document.addEventListener(CHANGE, render);
  render();
}

// The product block; Node (tests, comparison script) has no DOM, so a stand-in base there.
class BjerveButikklager extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
  connectedCallback() {
    const { handle, variantId, token } = this.dataset;
    // No token (kill switch) or the quick-add modal (spec 2.6): no choice.
    if (!handle || !variantId || !token || this.closest('quick-add-modal') || !this.radio('henting')) return;
    this.nodes = null;
    this.failed = false;
    for (const type of [CHANGE, 'change', 'input']) document.addEventListener(type, this);
    this.addEventListener('click', this);
    this.render();
    this.dataset.state = 'laster';
    requestStoreAvailability(handle, token)
      .then((byVariant) => {
        this.nodes = byVariant.get(variantId) ?? null;
        if (!this.nodes) throw new Error(`Storefront API: variant ${variantId} not in response`);
      })
      .catch((error) => {
        this.failed = true;
        console.warn(`bjerve-butikklager: ${error.message}`);
      })
      .finally(() => this.isConnected && this.render());
  }

  disconnectedCallback() {
    for (const type of [CHANGE, 'change', 'input']) document.removeEventListener(type, this);
  }

  handleEvent(event) {
    const choice = window.bjerveHenting;
    const { target } = event;
    if (event.type === CHANGE) {
      this.render();
    } else if (event.type === 'click') {
      if (target.closest('[data-bjerve-velg-by]') && this.pickup?.suggestion) choice?.velg('henting', this.pickup.suggestion.locationId);
      else if (target.closest('[data-bjerve-velg-butikk]')) this.openPicker(target.closest('button'));
    } else if (this.contains(target) && target.matches('input[type="radio"]')) {
      if (event.type !== 'change') return;
      if (target.value === 'hjem') choice?.velg('hjem');
      else if (this.pickup?.locationId) choice?.velg('henting', this.pickup.locationId);
      else this.openPicker(this.querySelector('[data-bjerve-velg-butikk]'));
    } else if (target === this.quantityInput()) {
      this.render();
    }
  }

  radio(value) {
    return this.querySelector(`input[type="radio"][value="${value}"]`);
  }

  quantityInput() {
    return (this.closest('product-info') ?? document).querySelector('.quantity__input, input[name="quantity"]');
  }

  quantity() {
    const asked = Number.parseInt(this.quantityInput()?.value ?? '1', 10);
    return Number.isFinite(asked) && asked >= 1 ? asked : 1;
  }

  openPicker(opener) {
    const { nodes } = this;
    const quantity = this.quantity();
    window.bjerveHenting?.apneVelger?.({
      apner: opener,
      lager: (id) => ({ tid: readyTime(nodes, id, quantity), antall: onHand(nodes, id) }),
      etterLukk: () => this.isConnected && this.render(),
    });
  }

  render() {
    const choice = window.bjerveHenting;
    const t = (name) => this.getAttribute(`data-t-${name}`) ?? '';
    const pickup = buildPickup(
      { nodes: this.nodes, locationId: choice?.locationId ?? null, quantity: this.quantity() },
      {
        pickup: t('pickup'), fewLeft: t('few-left'), klar10: t('klar-10'), travelt: t('travelt'), velgBy: t('velg-by'),
        ingenValgt: t('ingen-valgt'), velgButikk: t('velg-butikk'), endre: t('endre'), hold: '',
      },
      shopsOfPage()
    );
    this.pickup = pickup;
    show(this, 'henting-tekst', pickup.lines[0]);
    show(this, 'henting-fa', pickup.lines[1]);
    const offer = this.querySelector('[data-bjerve-travelt]');
    if (offer) offer.hidden = !pickup.suggestion;
    show(this, 'travelt-tekst', pickup.suggestion?.text);
    show(this, 'velg-by', pickup.suggestion?.button);
    show(this, 'velg-butikk', pickup.picker);

    const henting = choice?.mode === 'henting' && pickup.locationId !== null && pickup.locationId === choice.locationId;
    this.radio('henting').checked = henting;
    this.radio('hjem').checked = !henting;

    // Held at no shop: the home line promises the supplier's time.
    const delivery = this.querySelector('[data-bjerve-delivery-text]');
    if (delivery && this.nodes && !PICKUP_LOCATION_IDS.some((id) => onHand(this.nodes, id) > 0)) {
      delivery.textContent = t('delivery-none');
    }
    this.dataset.state = this.failed ? 'feil' : !pickup.locationId ? 'ingen' : pickup.time === '2-timer' ? 'holder' : 'ikke';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('bjerve-butikklager')) {
  customElements.define('bjerve-butikklager', BjerveButikklager);
}
